import { useState } from "react";
import { useRoute, Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Loader2, CheckCircle2, ArrowLeft, ShieldCheck } from "lucide-react";

export default function ResetPassword() {
  const [, params] = useRoute("/reset-password/:token");
  const token = params?.token || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { toast } = useToast();

  const passwordStrength = (): { label: string; color: string; pct: number } => {
    const len = password.length;
    if (len === 0) return { label: "", color: "", pct: 0 };
    if (len < 6) return { label: "Too short", color: "#ef4444", pct: 20 };
    if (len < 8) return { label: "Weak", color: "#f97316", pct: 40 };
    const hasUpper = /[A-Z]/.test(password);
    const hasNum = /[0-9]/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    const score = [hasUpper, hasNum, hasSpecial].filter(Boolean).length;
    if (score === 0) return { label: "Fair", color: "#eab308", pct: 50 };
    if (score === 1) return { label: "Good", color: "#22c55e", pct: 70 };
    return { label: "Strong", color: "#16a34a", pct: 100 };
  };

  const strength = passwordStrength();
  const passwordsMatch = confirm.length > 0 && password === confirm;
  const passwordsMismatch = confirm.length > 0 && password !== confirm;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      toast({ variant: "destructive", title: "Passwords don't match", description: "Please make sure both passwords are identical." });
      return;
    }
    if (password.length < 8) {
      toast({ variant: "destructive", title: "Password too short", description: "Password must be at least 8 characters." });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "This link may be invalid or expired.");
      }
      setDone(true);
    } catch (err: unknown) {
      toast({
        variant: "destructive",
        title: "Reset failed",
        description: (err as Error)?.message || "This link may be invalid or expired. Please request a new one.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md text-center">
          <div className="flex justify-center mb-6">
            <img src="/images/finproject-logo.png" alt="FinProject" className="w-14 h-14 object-contain" />
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">Password updated!</h1>
            <p className="text-slate-500 text-sm leading-relaxed mb-8">
              Your password has been changed successfully. You can now sign in with your new password.
            </p>
            <Link href="/app/login">
              <Button className="w-full h-11 rounded-xl font-semibold text-sm bg-blue-600 hover:bg-blue-700 text-white border-0">
                Sign In Now
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left branding panel */}
      <div
        className="hidden md:flex flex-col justify-center px-12 relative overflow-hidden"
        style={{
          flex: "0 0 45%",
          background: "linear-gradient(160deg, #1e3a5f 0%, #2563eb 50%, #1d4ed8 100%)",
        }}
      >
        <div className="relative z-10 max-w-sm mx-auto">
          <div className="mb-8">
            <img src="/images/finproject-logo.png" alt="FinProject" className="w-24 h-24 object-contain" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-2 tracking-tight">FinProject</h2>
          <p className="text-lg font-medium mb-10 text-blue-200">Construction Finance Platform</p>
          <div className="p-6 rounded-2xl" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)" }}>
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mb-4">
              <ShieldCheck className="w-5 h-5 text-blue-200" />
            </div>
            <p className="text-sm text-blue-100 leading-relaxed">
              Choose a strong password with at least 8 characters. Mix uppercase letters, numbers, and symbols for best security.
            </p>
          </div>
          <div className="mt-14 pt-8 border-t border-blue-700/40">
            <p className="text-xs text-blue-300/60">Trusted by construction finance teams nationwide</p>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-8 md:hidden">
            <img src="/images/finproject-logo.png" alt="FinProject" className="w-16 h-16 object-contain mb-3" />
            <span className="text-xl font-bold text-slate-900 mb-1">FinProject</span>
            <p className="text-sm text-slate-500">Construction Finance Platform</p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
            <div className="mb-7">
              <h1 className="text-2xl font-bold text-slate-900 mb-1">Set new password</h1>
              <p className="text-sm text-slate-500">Must be at least 8 characters long.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold tracking-wide uppercase text-slate-500">New Password</Label>
                <div className="relative">
                  <Input
                    type={showPwd ? "text" : "password"}
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="h-11 rounded-xl border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-blue-400 focus:ring-blue-400 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(!showPwd)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {password.length > 0 && (
                  <div className="space-y-1.5 pt-1">
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{ width: `${strength.pct}%`, background: strength.color }}
                      />
                    </div>
                    <p className="text-xs font-medium" style={{ color: strength.color }}>{strength.label}</p>
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold tracking-wide uppercase text-slate-500">Confirm Password</Label>
                <div className="relative">
                  <Input
                    type={showConfirm ? "text" : "password"}
                    required
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    placeholder="••••••••"
                    className={`h-11 rounded-xl bg-white text-slate-900 placeholder:text-slate-400 focus:ring-blue-400 pr-12 ${
                      passwordsMismatch
                        ? "border-red-300 focus:border-red-400"
                        : passwordsMatch
                          ? "border-green-400 focus:border-green-400"
                          : "border-slate-200 focus:border-blue-400"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {passwordsMismatch && (
                  <p className="text-xs text-red-500 font-medium">Passwords don't match</p>
                )}
                {passwordsMatch && (
                  <p className="text-xs text-green-600 font-medium flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Passwords match
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading || passwordsMismatch}
                className="w-full h-11 rounded-xl font-semibold text-sm bg-blue-600 hover:bg-blue-700 text-white border-0 mt-1"
              >
                {loading
                  ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Updating...</span>
                  : "Update Password"
                }
              </Button>
            </form>
          </div>

          <div className="mt-5 text-center">
            <Link href="/app/login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
